/**
 * AdvisorsTab Component (T133 - US6)
 * Complete financial advisor management interface
 * 
 * Integrates:
 * - AdvisorTable for displaying advisors
 * - Search input for name/firm filtering
 * - PaginationControls for navigation
 * - AdvisorForm for editing
 * - React Query hooks for data management
 * 
 * Note: No create functionality - advisors are created via user registration
 */

import { useState } from 'react';
import { Search, AlertCircle } from 'lucide-react';
import { AdvisorTable } from './AdvisorTable';
import { TrialMetricsCard } from './TrialMetricsCard';
import { AdvisorTableSkeleton } from './AdvisorTableSkeleton';
import { PaginationControls } from './PaginationControls';
import { AdvisorEditorPage } from './advisor-editor/AdvisorEditorPage';
import { usePagination } from '../hooks/usePagination';
import {
  useAdminAdvisors,
  useDeleteAdvisor,
} from '../hooks/useAdminAdvisors';
import type { AdminAdvisor, FirmSizeSegment } from '../types/admin-advisor.type';

// '' = "All" (no segment filter); 'UNASSIGNED' = advisors with no segment set.
type SegmentFilterValue = FirmSizeSegment | 'UNASSIGNED' | '';

/**
 * Advisors tab component with full management functionality
 * 
 * Features:
 * - List advisors with pagination
 * - Search by name or firm
 * - Edit existing advisors
 * - Soft-delete advisors
 * - Optimistic updates
 * - Loading states
 * - Error handling
 * - Total count reflects applied filters
 */
export function AdvisorsTab() {
  const [editAdvisorId, setEditAdvisorId] = useState<string | undefined>();

  // State management
  const [search, setSearch] = useState('');
  // Designation filters (Feature 071)
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilterValue>('');
  const [championOnly, setChampionOnly] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<AdminAdvisor | null>(null);

  // Pagination
  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 50,
  });

  // Data fetching
  const { data, isLoading, error, refetch } = useAdminAdvisors({
    page: pagination.page,
    pageSize: pagination.pageSize,
    search: search || undefined,
    firmSizeSegment: segmentFilter || undefined,
    isFirmChampion: championOnly || undefined,
  });

  const isFiltered = !!search || !!segmentFilter || championOnly;

  // Mutations
  const deleteAdvisor = useDeleteAdvisor();

  // Handlers
  const handleEditAdvisor = (advisorId: string) => {
    setEditAdvisorId(advisorId);
  };

  const handleDeleteAdvisor = (advisorId: string) => {
    const advisor = data?.items.find((a) => a.id === advisorId);
    if (advisor) {
      setDeleteConfirm(advisor);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteAdvisor.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      // Error is handled by React Query
      console.error('Delete error:', error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm(null);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    pagination.goToFirstPage(); // Reset to page 1 on search
  };

  const handleSegmentFilterChange = (value: SegmentFilterValue) => {
    setSegmentFilter(value);
    pagination.goToFirstPage();
  };

  const handleChampionOnlyChange = (value: boolean) => {
    setChampionOnly(value);
    pagination.goToFirstPage();
  };

  // Compute display name for delete confirmation
  const getAdvisorDisplayName = (advisor: AdminAdvisor): string => {
    return advisor.name || 
      `${advisor.user.firstName} ${advisor.user.lastName || ''}`.trim();
  };

  if (editAdvisorId) {
    return (
      <AdvisorEditorPage
        advisorId={editAdvisorId}
        onBack={() => {
          setEditAdvisorId(undefined);
          refetch();
        }}
      />
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Failed to load advisors
        </h3>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div data-testid="advisors-tab" className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Financial Advisors</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Manage financial advisor profiles
          </p>
        </div>
      </div>

      {/* Feature 100 — trial funnel (renders only once trials exist) */}
      <div className="px-6 pt-4">
        <TrialMetricsCard />
      </div>

      {/* Search + designation filters panel */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative max-w-md flex-1 min-w-[16rem]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by advisor name or firm..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent"
            />
          </div>

          {/* Firm size segment filter (Feature 071) */}
          <div className="flex items-center gap-2">
            <label htmlFor="segmentFilter" className="text-sm text-gray-600">
              Firm size
            </label>
            <select
              id="segmentFilter"
              value={segmentFilter}
              onChange={(e) =>
                handleSegmentFilterChange(e.target.value as SegmentFilterValue)
              }
              className="py-2 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857]"
            >
              <option value="">All</option>
              <option value="SMB">SMB</option>
              <option value="ENT">ENT</option>
              <option value="UNASSIGNED">Unassigned</option>
            </select>
          </div>

          {/* Firm Champion filter (Feature 071) */}
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={championOnly}
              onChange={(e) => handleChampionOnlyChange(e.target.checked)}
            />
            Firm Champions only
          </label>
        </div>
      </div>

      {/* Table content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {isLoading ? (
          <AdvisorTableSkeleton rows={pagination.pageSize} />
        ) : data ? (
          <AdvisorTable
            advisors={data.items}
            totalCount={data.meta.total}
            isFiltered={isFiltered}
            onEdit={handleEditAdvisor}
            onDelete={handleDeleteAdvisor}
          />
        ) : null}
      </div>

      {/* Pagination controls */}
      {data && (
        <PaginationControls
          meta={data.meta}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Financial Advisor
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium">
                {getAdvisorDisplayName(deleteConfirm)}
              </span>
              {' '}from{' '}
              <span className="font-medium">{deleteConfirm.companyName}</span>
              ? This action will soft-delete the advisor and they will no longer
              appear in lists.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                disabled={deleteAdvisor.isPending}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteAdvisor.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteAdvisor.isPending ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete Advisor</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
